import React, { useState } from 'react';
import ContractRentalDocumentBody from '../contracts/ContractRentalDocumentBody';
import { contractApi, type Contract } from '../../services/contractApi';
import { savePdfToDevice } from '../../utils/savePdfToDevice';
import { summaryFromContract } from '../../utils/contractTemplateSummary';

function contractFileName(contract: Contract) {
  const number = String(contract.template_document?.number || `CT-${String(contract.id).padStart(6, '0')}`).replace(
    /\s+/g,
    '',
  );
  return `hop-dong-${number}.pdf`;
}

type Props = {
  open: boolean;
  title?: string;
  onClose: () => void;
  contract: Contract | null;
  loading?: boolean;
  error?: string | null;
  success?: string | null;
  canSignLandlord?: boolean;
  canSignTenant?: boolean;
  signingRole?: 'landlord' | 'tenant' | null;
  onSaveSignature?: (role: 'landlord' | 'tenant', image: string) => Promise<void> | void;
  toolbar?: React.ReactNode;
  showDownloadPdf?: boolean;
  children?: React.ReactNode;
};

const MobileContractSignPopup: React.FC<Props> = ({
  open,
  title = 'Hợp đồng',
  onClose,
  contract,
  loading = false,
  error,
  success,
  canSignLandlord = false,
  canSignTenant = false,
  signingRole = null,
  onSaveSignature,
  toolbar,
  showDownloadPdf = false,
  children,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  if (!open) return null;

  const downloadContract = async () => {
    if (!contract) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      const blob = await contractApi.downloadContractPdf(contract.id);
      await savePdfToDevice(blob, contractFileName(contract));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Không tải được PDF.';
      setDownloadError(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-2 sm:p-4">
      <div className="flex h-[92dvh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-black/5 px-4 py-3">
          <h2 className="min-w-0 truncate text-base font-semibold text-black">{title}</h2>
          <div className="flex shrink-0 items-center gap-2">
            {toolbar}
            {showDownloadPdf && contract && (
              <button
                type="button"
                disabled={downloading}
                onClick={() => void downloadContract()}
                className="rounded-xl bg-[#C4A35A] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {downloading ? 'Đang tải…' : 'Tải PDF'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-black/10 px-3 py-1.5 text-sm font-medium text-black/70"
            >
              Đóng
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4">
          {error && <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          {success && (
            <p className="mb-3 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{success}</p>
          )}
          {downloadError && <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{downloadError}</p>}

          {loading && (
            <div className="flex justify-center py-10">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#C4A35A] border-t-transparent" />
            </div>
          )}

          {children}

          {contract && !loading && (
            <div className="pb-6">
              <p className="mb-3 text-sm text-black/60">
                {canSignLandlord || canSignTenant
                  ? 'Nhấn vào ô chữ ký để vẽ. Bấm Đồng ý rồi Xóa hoặc Xác nhận ngay dưới ô trên hợp đồng.'
                  : 'Xem hợp đồng. Chỉ chủ nhà được ký.'}
              </p>
              <ContractRentalDocumentBody
                summary={summaryFromContract(contract)}
                landlordSignature={contract.landlord_signature_image}
                tenantSignature={contract.tenant_signature_image}
                canSignLandlord={canSignLandlord}
                canSignTenant={canSignTenant}
                signingRole={signingRole}
                onSaveSignature={onSaveSignature}
                signInPopup
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileContractSignPopup;
